// @ts-nocheck
import { useState } from 'react';
import { Button, Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  MantineTransition,
  AnimatedGroup,
  PopTransition,
  CustomTransition,
} from '@/components/animations/mantine';

const MANTINE_TRANSITIONS = [
  'fade',
  'scale',
  'scale-y',
  'scale-x',
  'skew-up',
  'skew-down',
  'rotate-left',
  'rotate-right',
  'slide-down',
  'slide-up',
  'slide-left',
  'slide-right',
  'pop',
];

const GROUP_ITEMS = [
  { label: 'Dashboard', hint: 'Overview & KPIs' },
  { label: 'Members', hint: '1,240 records' },
  { label: 'Events', hint: '8 upcoming' },
  { label: 'Giving', hint: 'R 84,200 this month' },
];

export default function MantineTransitionsPage() {
  const [showMantine, setShowMantine] = useState(true);
  const [activeTransition, setActiveTransition] = useState('fade');
  const [showPop, setShowPop] = useState(true);
  const [showCustom, setShowCustom] = useState(true);
  const [groupKey, setGroupKey] = useState(0);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Mantine Transition Wrappers</Title>
        <Text c="dimmed" size="sm">
          Thin wrappers around Mantine's Transition component that add reduced-motion support and
          ergonomic props. Use the built-in transition names, the bouncy Pop preset, fully custom
          style transitions, or staggered group reveals.
        </Text>
      </Stack>

      {/* MantineTransition */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>MantineTransition</Title>
            <Text size="sm" c="dimmed">
              Wraps Mantine's Transition with a `visible` toggle and any of the built-in transition
              presets
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowMantine((v) => !v)}>Toggle</Button>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
              {MANTINE_TRANSITIONS.map((name) => (
                <Button
                  key={name}
                  size="xs"
                  variant={activeTransition === name ? 'filled' : 'light'}
                  onClick={() => setActiveTransition(name)}
                >
                  {name}
                </Button>
              ))}
            </SimpleGrid>
            <MantineTransition visible={showMantine} transition={activeTransition} duration={350}>
              <Card variant="subtle" p="md" ta="center">
                <Text fw={600}>
                  Animating with: <Code>{activeTransition}</Code>
                </Text>
                <Text size="sm" c="dimmed">
                  Toggle the button to replay the enter / exit animation
                </Text>
              </Card>
            </MantineTransition>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<MantineTransition visible={visible} transition="${activeTransition}" duration={350}>
  <Card variant="subtle" p="md">
    <Text>Animating content</Text>
  </Card>
</MantineTransition>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • visible - Mounts / unmounts the content (default: true)
              <br />
              • transition - Any built-in Mantine transition name (default: 'fade')
              <br />
              • duration - Enter duration in ms (default: 200)
              <br />
              • exitDuration - Exit duration in ms (defaults to duration)
              <br />
              • keepMounted - Keep node in the DOM while hidden (default: false)
              <br />
              • onEnter / onEntered / onExit / onExited - Lifecycle callbacks
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PopTransition */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PopTransition</Title>
            <Text size="sm" c="dimmed">
              The `pop` transition with a spring-like cubic-bezier easing for a bouncy entrance
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowPop((v) => !v)}>Toggle Pop</Button>
            </Group>
            <SimpleGrid cols={3} spacing="md">
              <PopTransition visible={showPop} duration={300} delay={0}>
                <Card variant="outlined" p="md" ta="center">
                  <Text fw={600}>New</Text>
                  <Text size="xs" c="dimmed">no delay</Text>
                </Card>
              </PopTransition>
              <PopTransition visible={showPop} duration={300} delay={120}>
                <Card variant="outlined" p="md" ta="center">
                  <Text fw={600}>Badge</Text>
                  <Text size="xs" c="dimmed">120ms delay</Text>
                </Card>
              </PopTransition>
              <PopTransition visible={showPop} duration={300} delay={240}>
                <Card variant="outlined" p="md" ta="center">
                  <Text fw={600}>Toast</Text>
                  <Text size="xs" c="dimmed">240ms delay</Text>
                </Card>
              </PopTransition>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PopTransition visible={visible} duration={300} delay={120}>
  <Card variant="outlined" p="md">
    <Text>Badge</Text>
  </Card>
</PopTransition>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • visible - Mounts / unmounts the content (default: true)
              <br />
              • duration - Animation duration in ms (default: 200)
              <br />
              • delay - Delay before the animation starts in ms (default: 0)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* CustomTransition */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CustomTransition</Title>
            <Text size="sm" c="dimmed">
              Define your own enter / exit styles for full control over the animation
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowCustom((v) => !v)}>Toggle Custom</Button>
            </Group>
            <CustomTransition
              visible={showCustom}
              duration={400}
              styles={{
                in: { opacity: 1, transform: 'translateY(0) scale(1)' },
                out: { opacity: 0, transform: 'translateY(24px) scale(0.92)' },
                common: { transformOrigin: 'bottom center' },
                transitionProperty: 'transform, opacity',
              }}
            >
              <Card variant="gradient" gradientVariant="primary" p="md" ta="center">
                <Text c="white" fw={600}>Slide up + fade + scale</Text>
                <Text c="white" size="sm">A combined custom transition</Text>
              </Card>
            </CustomTransition>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<CustomTransition
  visible={visible}
  duration={400}
  styles={{
    in: { opacity: 1, transform: 'translateY(0) scale(1)' },
    out: { opacity: 0, transform: 'translateY(24px) scale(0.92)' },
    common: { transformOrigin: 'bottom center' },
    transitionProperty: 'transform, opacity',
  }}
>
  <Card variant="gradient" gradientVariant="primary">
    <Text c="white">Slide up + fade + scale</Text>
  </Card>
</CustomTransition>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • styles - {`{ in, out, common?, transitionProperty? }`} CSS style objects
              <br />
              • visible - Mounts / unmounts the content (default: true)
              <br />
              • duration - Animation duration in ms (default: 200)
              <br />
              • delay - Delay before the animation starts in ms (default: 0)
              <br />
              • timingFunction - CSS easing function (default: 'ease')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* AnimatedGroup */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>AnimatedGroup</Title>
            <Text size="sm" c="dimmed">
              Reveals a list of children with a staggered delay between each item
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setGroupKey((k) => k + 1)}>Replay Stagger</Button>
            </Group>
            <AnimatedGroup key={groupKey} transition="slide-right" duration={300} staggerDelay={120}>
              {GROUP_ITEMS.map((item) => (
                <Card key={item.label} variant="subtle" p="sm" mb="xs">
                  <Group justify="space-between">
                    <Text fw={600}>{item.label}</Text>
                    <Text size="sm" c="dimmed">{item.hint}</Text>
                  </Group>
                </Card>
              ))}
            </AnimatedGroup>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<AnimatedGroup transition="slide-right" duration={300} staggerDelay={120}>
  {items.map((item) => (
    <Card key={item.label} variant="subtle" p="sm" mb="xs">
      <Text>{item.label}</Text>
    </Card>
  ))}
</AnimatedGroup>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • children - The items to reveal in sequence
              <br />
              • transition - Built-in Mantine transition name (default: 'fade')
              <br />
              • duration - Per-item animation duration in ms (default: 200)
              <br />
              • staggerDelay - Delay added per item index in ms (default: 50)
            </Text>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Dropdowns & Popovers</Text>
              <Text size="xs" c="dimmed">
                Use MantineTransition with 'pop' or 'scale-y' to match menus to their trigger.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Notifications & Badges</Text>
              <Text size="xs" c="dimmed">
                PopTransition gives toasts and count badges a playful, attention-grabbing entrance.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Bespoke Motion</Text>
              <Text size="xs" c="dimmed">
                CustomTransition lets you combine transform and opacity for one-off, branded effects.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>List Reveals</Text>
              <Text size="xs" c="dimmed">
                AnimatedGroup staggers cards or table rows so dashboards feel alive on first load.
              </Text>
            </Stack>
          </SimpleGrid>

          <Text size="xs" c="dimmed">
            All wrappers honor the user's prefers-reduced-motion setting and collapse animation
            durations to 0 when reduced motion is requested.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
